import { useCallback } from 'react';

import { resolve411Number } from 'features/call/call-hearing/helpers/';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { isDeafToHearingSelector } from 'features/call/call-base/store/selectors';
import { deafPhoneNumberSelector } from 'features/call/call-deaf/store';
import { primaryHearingPhoneNumberSelector } from 'features/call/call-hearing/store';
import { AppConfigService } from 'features/app-config/services';
import { N11DaoService } from 'features/n11/services';
import { isSpecialPhoneNumber } from 'features/n11/helpers/';
import { sendAnalyticsError } from 'features/analytics/helpers';

interface N11Response {
  phoneNumber: string;
  n11Status: 0 | 1;
}

const CRISIS_NUMBER = '988';
const DIRECTORY_ASSISTANCE = '411';
const N11_STATUS_SUCCESS = 0;
const N11_STATUS_ERROR = 1;

/**
 * Hook for resolving N11 and special numbers with D2H 411 support
 * @returns Object with resolve function
 */
export const useN11Resolver = () => {
  const isDeafToHearing = useAppSelector(isDeafToHearingSelector);
  const deafPhoneNumber = useAppSelector(deafPhoneNumberSelector);
  const primaryHearingPhoneNumber = useAppSelector(
    primaryHearingPhoneNumberSelector
  );
  const dispatch = useAppDispatch();

  const resolve = useCallback(
    async (originalNumber: string, callId: number): Promise<N11Response> => {
      if (!originalNumber || !isSpecialPhoneNumber(originalNumber)) {
        return {
          phoneNumber: originalNumber || '',
          n11Status: N11_STATUS_ERROR,
        };
      }

      // Handle 988 crisis number
      if (originalNumber === CRISIS_NUMBER) {
        const suicidePreventionNumber =
          AppConfigService.suicidePreventionNumber;
        return {
          phoneNumber: suicidePreventionNumber,
          n11Status: suicidePreventionNumber.length
            ? N11_STATUS_SUCCESS
            : N11_STATUS_ERROR,
        };
      }

      // Handle 411 for D2H calls only - DO NOT use DAO service
      if (originalNumber === DIRECTORY_ASSISTANCE) {
        if (!isDeafToHearing) {
          return { phoneNumber: originalNumber, n11Status: N11_STATUS_ERROR };
        }

        // Fall back to primary hearing when the deaf connection string is not resolvable.
        const resolvedFromDeaf =
          deafPhoneNumber && resolve411Number(deafPhoneNumber);
        const resolvedFromPrimary =
          primaryHearingPhoneNumber &&
          resolve411Number(primaryHearingPhoneNumber);
        const resolved411Number = resolvedFromDeaf || resolvedFromPrimary;

        if (resolved411Number) {
          return {
            phoneNumber: resolved411Number,
            n11Status: N11_STATUS_SUCCESS,
          };
        }

        // If we can't resolve the area code, return error status
        return { phoneNumber: originalNumber, n11Status: N11_STATUS_ERROR };
      }

      // Handle other N11 numbers (211, 311, 511, 711, 811) through standard DAO resolution
      try {
        const { PhoneNumber, N11Status } = await N11DaoService.getN11Number({
          phoneNumber: originalNumber,
          callId,
        });
        return { phoneNumber: PhoneNumber, n11Status: N11Status };
      } catch (error) {
        dispatch(
          sendAnalyticsError({
            error,
            message: 'Error resolving N11 number',
          })
        );
        return { phoneNumber: originalNumber, n11Status: N11_STATUS_ERROR };
      }
    },
    [isDeafToHearing, deafPhoneNumber, primaryHearingPhoneNumber, dispatch]
  );

  return { resolve };
};
